import {
  useState,
  useRef,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  useReducer,
} from "react";
import produce from "immer";

//Components
import DropZone from "../DropZone";
import SingleDropDown from "../SingleDropDown";
import CheckboxWithLabel from "../CheckboxWithLabel";
import {
  FormControl,
  TextField,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { DateTimePicker } from "@material-ui/pickers";
import deleteButton from "../../Assets/deleteButton.png";
import addButton from "../../Assets/addButton.png";

//Utils
import PropTypes from "prop-types";
import { useCookies } from "react-cookie";

//styling
import { checkPermission } from "../../utils/Roles";
import { useLazyQuery, useMutation } from "@apollo/client";
import { GET_UNAUTHORIZED_ATTACHMENT_URL } from "../../Queries/GetUnauthorizedAttachmentUrl";
import { UserContext } from "../../Contexts/UserContext";
import { useFileUpload } from "../../CustomHooks/useFileUpload";
import { useStyles } from "../../Styles/styles";
import { SetupContext } from "../../Contexts/SetupContext";
import { UPDATE_ATTACHMENT } from "../../Mutations/UpdateAttachment";
import {
  convertMomentTimeToDateString,
  localGetData,
  parseObjectArrayIdString,
} from "../../utils/utils";
import { REMOVE_ATTACHMENT } from "./RemoveAttachment";
import { GET_INCIDENT_DETAIL_ATTACHMENTS } from "./GetIncidentDetailAttachments";
import { GET_PERSON_ATTACHMENTS } from "./GetPersonAttachments";
import { createEmptyGenericAttachment } from "../../utils/EmptyObjects";
import { ADD_AUDIT_LOG } from "../../Mutations/AddAuditLog";

import moment from "moment";

const initialState = [];

function reducer(draft, action) {
  switch (action.type) {
    case "SET_ATTACHMENTS":
      return action.payload;

    case "ADD_ATTACHMENT":
      draft.push(action.payload);
      return;

    //finding the object in the array and mutating it directly is the preferred method when using Immer
    case "UPDATE_ATTACHMENT": {
      const attachment = draft.find(
        a => a.id === action.payload.id || a.id === action.payload.uuid
      );
      if (attachment) {
        Object.assign(attachment, action.payload);
      }
      return;
    }

    case "REMOVE_ATTACHMENT": {
      const index = draft.findIndex(a => a.id === action.payload.id);
      if (index !== -1) {
        draft.splice(index, 1);
      }
      return;
    }

    default:
      alert("Invalid action type in Attachment reducer: ", action.type);
      return;
  }
}

function attachmentsUrl(restHost, uploadClass, id) {
  const type = uploadClass === "Incident" ? "incidents" : "people";
  return `${restHost}/${type}/${id}/generic_attachments`;
}

function canViewConfidentialAttachment({
  attachment,
  userRoles,
  secondarySiblingIds,
  oriId,
  incidentId,
  userId,
  primaryOfficerId,
}) {
  const hasSuperUserRole = userRoles?.some(
    role => role?.toUpperCase() === "SUPER_USER"
  );
  return (
    ((!!oriId || hasSuperUserRole) &&
      checkPermission(
        userRoles,
        "view_confidential_attachments",
        secondarySiblingIds,
        oriId,
        incidentId?.toString()
      )) ||
    attachment?.employee?.id?.toString() === userId?.toString() ||
    (incidentId != null && incidentId !== "" && primaryOfficerId != null &&
      primaryOfficerId.toString() === userId?.toString())
  );
}

const Attachments = ({
  parentId = null,
  parentName = "",
  incidentId = "",

  divStyle = null,
  formStyle = null,

  uploadClass = "Incident",
  secondarySiblingIds = "",

  memoizedLocked = true,
  userId = null,
  primaryOfficerId = null,
  userRoles = [],
  online = true,
  ...props
}) => {
  const classes = useStyles();
  const api_host = import.meta.env.VITE_REST_HOST;
  const url = attachmentsUrl(api_host, uploadClass, parentId);

  const { uploadFile } = useFileUpload(url);

  const [attachments, dispatch] = useReducer(produce(reducer), initialState);

  const classToQuery = {
    Incident: GET_INCIDENT_DETAIL_ATTACHMENTS,
    Person: GET_PERSON_ATTACHMENTS,
  };

  const query = classToQuery[uploadClass] || null;

  const [getAttachments, { loading, error }] = useLazyQuery(query, {
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "network-only",
    onCompleted: res => {
      const attachments =
        res?.initialCallInfo?.incidentDetail?.genericAttachments ||
        res?.getPerson?.genericAttachments;
      if (attachments && attachments.length > 0) {
        dispatch({ type: "SET_ATTACHMENTS", payload: attachments });
      } else {
        handleAddLocalAttachment();
      }
    },
  });

  const [addAuditLog] = useMutation(ADD_AUDIT_LOG, {
    context: { serializationKey: "incidentQueue" },
  });

  const handleAddLocalAttachment = useCallback(() => {
    const emptyAttachment = createEmptyGenericAttachment();
    dispatch({ type: "ADD_ATTACHMENT", payload: emptyAttachment });
  }, []);

  const handleUpload = async (files = [], uploadInfo) => {
    const results = [];
    for (const file of files) {
      const formData = new FormData();
      const token = localGetData("mp_token");
      formData.append("authenticity_token", encodeURIComponent(token));
      formData.append("file", file);
      formData.append("klass", uploadClass);
      formData.append("id", parentId);
      formData.append("employee_id", userId);
      formData.append("confidential", uploadInfo?.confidential || false);
      formData.append(
        "attachment_type_id",
        uploadInfo?.attachmentType?.id || ""
      );
      formData.append("description", uploadInfo?.description || "");
      formData.append("source", "Mobile");

      try {
        const response = await uploadFile(formData);
        if (response.errors?.length > 0) {
          alert(
            `There was an error uploading the file:\n${response.errors.join(
              "\n"
            )}`
          );
        }
        if (response.status === 200 && response.attachment_id) {
          //instead use the attachment ID to fetch the attachment and replace it
          // getAttachment({ variables: { id: response.attachment_id, originatorId: userId } })
          //getAttachment will update the attachment ID from the uuid to the id from the database

          const message = `Attachment ${file.name} uploaded to ${parentName}`;
          addAuditLog({
            variables: {
              input: {
                incidentId: uploadClass === "Incident" ? parentId : incidentId,
                originatorId: userId,
                message,
                action: "Attachment uploaded",
              },
            },
            context: {
              serializationKey: "incidentQueue",
            },
          });
        }
        let queryParamName =
          uploadClass === "Incident" ? "incidentId" : "personId";
        getAttachments({
          variables: { originatorId: userId, [queryParamName]: parentId },
        });
        results.push({
          ok: true,
          fileName: file.name,
          response,
        });
      } catch (error) {
        console.error(error);
        alert("There was an error uploading the file");
        results.push({
          ok: false,
          fileName: file.name,
          error,
        });
      }
    }
    return results;
  };

  //useEffect to fire the lazyQuery with the correct variables when the component mounts
  useEffect(() => {
    if (parentId) {
      let variables = { originatorId: userId };
      if (uploadClass === "Incident") {
        variables.incidentId = parentId;
      } else if (uploadClass === "Person") {
        variables.personId = parentId;
      } else {
        return null;
      }
      if (online && query && (variables.personId || variables.incidentId)) {
        getAttachments({ variables });
      }
    }
  }, [getAttachments, online, parentId, query, uploadClass, userId]);

  if (loading) return <CircularProgress />;
  if (!attachments || !online) return null;
  if (error) return <p>Error: {error?.message}</p>;

  return (
    // <div style={divStyle}>
    <div
      style={{ width: "100%" }}
      id="attachment-container"
      data-cy="attachment-container"
    >
      <div className={classes.flexRow} style={{ alignItems: "center" }}>
        <button
          type="button"
          onClick={!memoizedLocked ? () => handleAddLocalAttachment() : null}
          disabled={memoizedLocked}
          style={{ background: "none", border: 0, padding: 0 }}
        >
          <img
            data-cy={"add-narrative-button"}
            src={addButton}
            className={classes.pngButton}
            alt="Add Attachment"
          />
        </button>
        <Typography
          style={{ width: "100%", margin: "0 16px", fontWeight: "bold" }}
          data-cy="person-header"
        >
          Attachments{" "}
        </Typography>
      </div>
      {attachments?.map((attachment, index) => (
        <Attachment
          divStyling={divStyle}
          formStyling={formStyle}
          attachment={attachment}
          numOfAttachments={attachments?.length}
          attachmentIndex={index}
          parentId={parentId}
          parentName={parentName}
          incidentId={incidentId}
          key={attachment.id}
          addAttachmentHandler={handleAddLocalAttachment}
          dispatch={dispatch}
          oriId={
            attachment?.incident?.ori?.id ||
            attachment?.employee?.deptAttribute?.ori?.id
          }
          secondarySiblingIds={secondarySiblingIds}
          userId={userId}
          primaryOfficerId={primaryOfficerId}
          memoizedLocked={memoizedLocked}
          userRoles={userRoles}
          uploadClass={uploadClass}
          handleUpload={handleUpload}
          {...props}
        />
      ))}
    </div>
  );
};

const Attachment = ({
  attachment = {
    id: "",
    attachmentType: "",
    description: "",
    uploadedAt: "",
  },
  attachmentIndex = 0,
  parentName = "",
  parentId = null,
  incidentId = "",
  numOfAttachments = 1,
  dispatch = () => {
    alert("No dispatch function provided");
  },
  oriId = "",
  secondarySiblingIds = "",
  userId = "",
  primaryOfficerId = null,
  memoizedLocked = true,
  userRoles = [],
  uploadClass = "Incident",
  divStyling = "",
  formStyling = "",
  handleUpload = () => alert("No handleUpload function passed"),
  ...props
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadState, setUploadState] = useState({
    loading: false,
    progress: 0,
    error: null,
    successFileName: "",
  });
  const [cookies] = useCookies();
  const classes = useStyles();
  const divStyle =
    divStyling || cookies[`mp-userId-${userId}-night-mode`] === "true"
      ? classes.darkDiv
      : classes.greyDiv;
  const formStyle = formStyling || classes.formControl;

  const { setup } = useContext(SetupContext);
  const { attachmentTypes } = setup.setupLists;
  const attachmentIncidentId = uploadClass === "Incident" ? parentId : incidentId;
  const hasAttachedFile = Boolean(
    attachment?.dataFileName || attachment?.dataUrl || attachment?.dataUrlWrapper
  );
  const isOfflineDraft = attachment?.id?.toString().startsWith("Offline_") &&
    !hasAttachedFile;
  const canViewConfidential = !isOfflineDraft && canViewConfidentialAttachment({
    attachment,
    userRoles,
    secondarySiblingIds,
    oriId,
    incidentId: attachmentIncidentId,
    userId,
    primaryOfficerId,
  });
  const canClearConfidential = isOfflineDraft || canViewConfidential;
  const cannotEditAttachedFile = hasAttachedFile && attachment?.confidential &&
    !canViewConfidential;
  const attachmentLocked = memoizedLocked || cannotEditAttachedFile;
  const deleteLocked = !isOfflineDraft &&
    (attachmentLocked || (attachment?.confidential && !canViewConfidential));

  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);

  const [getUnauthorizedAttachmentUrl] = useLazyQuery(
    GET_UNAUTHORIZED_ATTACHMENT_URL,
    {
      context: { serializationKey: "incidentQueue" },
      variables: {
        originatorId: userId,
        id: attachment?.id,
        incidentId: uploadClass === "Person" && incidentId ? incidentId : null,
      },
      fetchPolicy: "network-only",
      onCompleted: res => {
        const { redirectUrl, error } = res?.getUnauthorizedAttachmentUrl || {};
        if (error) {
          alert(error);
          return null;
        } else if (redirectUrl) {
          window.open(redirectUrl, "_blank");
        } else {
          alert("No URL found for this document");
        }
      },
      onError: err => {
        throw err;
      },
    }
  );

  const [addAuditLog] = useMutation(ADD_AUDIT_LOG, {
    context: { serializationKey: "incidentQueue" },
  });

  const removeAttachmentHandler = id => {
    if (deleteLocked) return;
    if (window.confirm("Are you sure you want to delete this attachment?")) {
      if (isOfflineDraft) {
        dispatch({ type: "REMOVE_ATTACHMENT", payload: { id } });
      } else {
        try {
          removeAttachment({
            variables: { input: {
              id,
              originatorId: userId,
              ...(uploadClass === "Person" && incidentId ? { incidentId } : {}),
            } },
          }).then(resp => {
            const { errors } = resp?.data?.removeAttachment || [];
            if (errors?.length <= 0) {
              const message = `Attachment ${attachment?.dataFileName} removed from ${parentName}`;
              addAuditLog({
                variables: {
                  input: {
                    incidentId:
                      uploadClass === "Incident" ? parentId : incidentId,
                    originatorId: userId,
                    message,
                    action: "Attachment removed",
                  },
                },
                context: {
                  serializationKey: "incidentQueue",
                },
              });
              dispatch({
                type: "REMOVE_ATTACHMENT",
                payload: { id: attachment.id },
              });
            }
          });
        } catch (e) {
          alert("There was an error removing the attachment");
        }
      }
    }
  };

  const [updateAttachment] = useMutation(UPDATE_ATTACHMENT, {
    context: { serializationKey: "incidentQueue" },
    onCompleted: res => {
      const { errors } = res?.updateAttachment || {};
      if (errors?.length > 0) {
        alert(
          `There was an error updating the attachment:\n${errors.join(
            "\n"
          )}`
        );
        return null;
      }
    },
    onError: err => {
      throw err;
    },
  });

  const [removeAttachment] = useMutation(REMOVE_ATTACHMENT, {
    context: { serializationKey: "incidentQueue" },
    onCompleted: res => {
      const { errors } = res?.removeAttachment || {};
      if (errors?.length > 0) {
        alert(
          `There was an error removing the attachment:\n${errors.join(
            "\n"
          )}`
        );
        return null;
      }
    },
    onError: err => {
      throw err;
    },
  });

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, []);

  const handleChange = useCallback(
    (event, submit = true) => {
      if (attachmentLocked) return;
      const updateAttachmentInputs = {
        attachmentType: {
          name: "attachmentTypeId",
          accessId: true,
          hasMany: false,
        },
        description: {
          name: "description",
          accessId: false,
          hasMany: false,
        },
        confidential: {
          name: "confidential",
          accessId: false,
          hasMany: false,
        },
      };
      let value;
      let dispatchName;
      if (event.target.type === "checkbox") {
        value = event.target.checked;
        dispatchName = event.target.value;
      } else {
        dispatchName = event.target.name;
        value = event.target.value;
      }

      let mutationValue = value;
      const mutationInformation = updateAttachmentInputs[dispatchName];
      const { name, accessId, hasMany } = mutationInformation;
      if (hasMany && accessId) {
        mutationValue = parseObjectArrayIdString(value);
      }
      if (accessId && !hasMany) {
        mutationValue = +value?.id || "";
      }
      if (moment.isMoment(value)) {
        mutationValue = convertMomentTimeToDateString(value);
      }

      let input = {
        originatorId: userId,
        id: attachment.id,
        [name]: mutationValue,
      };
      let payload = { id: attachment.id, [dispatchName]: value };

      //if the attachmentType name is criminal record, set the attachment to confidential
      if (
        dispatchName === "attachmentType" &&
        value?.name === "Criminal Record"
      ) {
        payload = { ...payload, confidential: true };
        input = { ...input, confidential: true };
      }

      if (input.confidential === false && attachment.confidential && !canClearConfidential) {
        return;
      }
      if (input.confidential === true && !attachment.confidential &&
          !window.confirm("Mark this attachment as confidential?")) {
        return;
      }
      if ((hasAttachedFile || input.confidential === false) &&
          uploadClass === "Person" && incidentId) {
        input.incidentId = incidentId;
      }

      const restoreConfidentialChange = () => {
        if (!Object.prototype.hasOwnProperty.call(input, "confidential")) return;
        dispatch({
          type: "UPDATE_ATTACHMENT",
          payload: {
            id: attachment.id,
            confidential: attachment.confidential,
            ...(dispatchName === "attachmentType"
              ? { attachmentType: attachment.attachmentType }
              : {}),
          },
        });
      };

      dispatch({ type: "UPDATE_ATTACHMENT", payload });
      if (!mutationInformation || !submit) return;
      try {
        updateAttachment({ variables: { input } })
          .then(response => {
            if (response?.data?.updateAttachment?.errors?.length)
              restoreConfidentialChange();
          })
          .catch(e => {
            restoreConfidentialChange();
            console.error(e);
          });
      } catch (e) {
        restoreConfidentialChange();
        console.error(e);
      }
    },
    [dispatch, attachment.id, attachment.confidential, attachment.attachmentType,
      attachmentLocked, canClearConfidential, hasAttachedFile, incidentId, updateAttachment,
      uploadClass, userId]
  );

  // useEffect(() => {
  //   if (attachment?.attachmentType?.name === 'Criminal Record') {
  //     const mockEvent = {
  //       target: {
  //         type: 'checkbox',
  //         value: 'confidential',
  //         checked: true
  //       }
  //     }
  //     handleChange(mockEvent)
  //   }
  // }, [attachment?.attachmentType, handleChange])

  const hasId = !attachment?.id?.includes("Offline");
  const onUpload = async () => {
    setUploadState({
      loading: true,
      progress: 0,
      error: null,
      successFileName: "",
    });
    const results = await handleUpload(selectedFiles, attachment);
    const firstError = results?.find(result => !result.ok);
    const firstSuccess = results?.find(result => result.ok);
    setUploadState({
      loading: false,
      progress: 100,
      error: firstError?.error || null,
      successFileName: firstSuccess?.fileName || "",
    });
    if (firstSuccess) {
      setSelectedFiles([]);
    }
  };
  return (
    <div
      id={"Incident-detail-attachment-" + attachment.id}
      className={divStyle}
      style={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        border: "1px solid darkgrey",
        marginBottom: attachmentIndex + 1 !== numOfAttachments ? "15px" : 0,
        alignItems: "flex-start",
        padding: "0 10px",
      }}
    >
      <SingleDropDown
        handleChange={e => handleChange(e, hasId)}
        stateName={"attachmentType"}
        value={attachment?.attachmentType}
        options={attachmentTypes}
        title={"Attachment Type"}
        classStyle={formStyle}
        inputLabel={inputLabel}
        labelWidth={labelWidth}
        disabled={attachmentLocked}
        userRoles={userRoles}
        {...props}
      />
      <FormControl variant="standard" className={formStyle}>
        <TextField
          data-cy="description"
          inputProps={{ "data-cy": "description-input" }}
          InputLabelProps={{
            shrink: true,
            "data-cy": "description-input-label",
          }}
          margin="dense"
          id="description-id"
          label="Description"
          value={attachment?.description || ""}
          name="description"
          onChange={e => handleChange(e, false)}
          onBlur={e => handleChange(e, hasId)}
          disabled={attachmentLocked}
        />
      </FormControl>
      <FormControl variant="standard" className={formStyle}>
        <DateTimePicker
          data-cy="lastUpdated-picker"
          clearable
          variant="dialog"
          label="Uploaded At"
          value={
            attachment?.lastUpdated
              ? moment
                  .utc(attachment?.lastUpdated, "YYYY-MM-DD HH:mm:ss UTC")
                  .local()
              : null
          }
          name="lastUpdated"
          format="MM/DD/YYYY HH:mm"
          ampm={false}
          InputLabelProps={{ shrink: true }}
          disabled={true}
        />
      </FormControl>
      <FormControl variant="standard" className={formStyle}>
        <TextField
          data-cy="ownerName"
          inputProps={{ "data-cy": "ownerName-input" }}
          InputLabelProps={{ shrink: true, "data-cy": "ownerName-input-label" }}
          margin="dense"
          id="ownerName-id"
          label="Employee Name"
          value={attachment?.ownerName || ""}
          name="ownerName"
          onChange={e => handleChange(e, hasId)}
          disabled={true}
        />
      </FormControl>
      <CheckboxWithLabel
        formStyle={formStyle}
        checked={attachment?.confidential || false}
        onChange={e => handleChange(e, hasId)}
        stateName={"confidential"}
        title={"Confidential"}
        userRoles={userRoles}
        {...props}
        disabled={
          attachmentLocked ||
          attachment?.attachmentType?.name === "Criminal Record" ||
          (attachment?.confidential && !canClearConfidential)
        }
      />
      {attachment?.id && (attachment?.dataUrlWrapper || attachment?.dataFileName) && (
        <AttachmentButton
          getUnauthorizedAttachmentUrl={getUnauthorizedAttachmentUrl}
          userRoles={userRoles}
          secondarySiblingIds={secondarySiblingIds}
          oriId={oriId}
          incidentId={attachmentIncidentId}
          attachment={attachment}
          userId={userId}
          primaryOfficerId={primaryOfficerId}
          formStyle={formStyle}
        />
      )}
      {!hasAttachedFile && <DropZone setSelectedFiles={setSelectedFiles} />}
      {!hasAttachedFile && (
        <button
          disabled={selectedFiles?.length === 0 || uploadState.loading}
          onClick={onUpload}
        >
          Upload
        </button>
      )}
      {uploadState.loading && <p>Uploading... {uploadState.progress}%</p>}
      {uploadState.error && <p>Error: {uploadState.error?.message}</p>}
      {uploadState.successFileName && (
        <p>Upload successful: {uploadState.successFileName}</p>
      )}
      <FormControl
        style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        className={formStyle}
      >
        {!deleteLocked && (
          <button
            type="button"
            onClick={() => removeAttachmentHandler(attachment.id)}
            style={{ background: "none", border: 0, padding: 0 }}
          >
            <img
              data-cy={"delete-attachment-button-" + attachment?.id}
              src={deleteButton}
              className={classes.pngButton}
              alt="Delete Attachment"
            />
          </button>
        )}
      </FormControl>
    </div>
  );
};

const AttachmentButton = ({
  userRoles,
  secondarySiblingIds,
  oriId,
  incidentId,
  attachment,
  userId,
  primaryOfficerId,
  formStyle,
  getUnauthorizedAttachmentUrl,
}) => {
  const showConfidentialLink = useMemo(() => {
    return canViewConfidentialAttachment({
      attachment, userRoles, secondarySiblingIds, oriId, incidentId,
      userId, primaryOfficerId,
    });
  }, [
    userRoles,
    secondarySiblingIds,
    oriId,
    userId,
    primaryOfficerId,
    attachment,
    incidentId,
  ]);
  const { nightMode } = useContext(UserContext);

  const buttonStyle = {
    visibility:
      attachment?.confidential && !showConfidentialLink ? "hidden" : "visible",
    maxWidth: "20vw",
    overflowWrap: "anywhere",
    background: "transparent",
    border: "none",
    color: nightMode ? "white" : "black",
    textDecoration: "underline",
    cursor: "pointer",
  };
  return (
    <FormControl
      variant="standard"
      className={formStyle}
      style={{ marginTop: "auto", marginBottom: "auto", width: "150px" }}
    >
      <button
        style={buttonStyle}
        data-cy="dataUrl-button"
        onClick={getUnauthorizedAttachmentUrl}
      >
        {attachment?.dataFileName || "File Link"}
      </button>
    </FormControl>
  );
};

Attachments.propTypes = {
  parentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  parentName: PropTypes.string,
  incidentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  divStyle: PropTypes.string,
  formStyle: PropTypes.string,
  uploadClass: PropTypes.string,
  secondarySiblingIds: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  memoizedLocked: PropTypes.bool,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  primaryOfficerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userRoles: PropTypes.array,
  online: PropTypes.bool,
};

AttachmentButton.propTypes = {
  userRoles: PropTypes.array,
  secondarySiblingIds: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  oriId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  incidentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  attachment: PropTypes.shape({
    dataFileName: PropTypes.string,
    confidential: PropTypes.bool,
    employee: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  primaryOfficerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  formStyle: PropTypes.string,
  getUnauthorizedAttachmentUrl: PropTypes.func,
};

Attachment.propTypes = {
  attachment: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    attachmentType: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
    description: PropTypes.string,
    lastUpdated: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    ownerName: PropTypes.string,
    confidential: PropTypes.bool,
    dataUrlWrapper: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    dataUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    dataFileName: PropTypes.string,
    employee: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  attachmentIndex: PropTypes.number,
  parentName: PropTypes.string,
  parentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  incidentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  numOfAttachments: PropTypes.number,
  dispatch: PropTypes.func,
  oriId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  secondarySiblingIds: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  primaryOfficerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  memoizedLocked: PropTypes.bool,
  userRoles: PropTypes.array,
  uploadClass: PropTypes.string,
  divStyling: PropTypes.string,
  formStyling: PropTypes.string,
  handleUpload: PropTypes.func,
};

export { Attachment, AttachmentButton };
export default Attachments;
